import {
  useV2CreateGovernanceMutation,
  useV2UpdateGovernanceMutation,
  useV2DeleteGovernanceMutation,
  useV2RestoreGovernanceByIdMutation,
  useV2PurgeGovernanceByIdMutation,
  useV2ChangeGovernancePositionByIdMutation,
} from "@generated";
import type { GovernanceFormValues } from "../types/Governance";
import { rateRulesToApi } from "@components/GovernanceEffort/rateRules";
import { useConfigurableGovernance } from "@components/GovernanceEffort/useConfigurableGovernance";
import { governanceVariablesToApi } from "./governanceVariables";

// With release.configurable-governance off, minimum-hours and rate-rules are
// left out entirely so a save never overwrites values set elsewhere.
export const governanceAttributes = (
  values: GovernanceFormValues,
  configurable: boolean
) => ({
  name: values.name,
  required: values.required,
  "calculation-type": values.calculationType,
  "fixed-hours":
    values.calculationType === "fixed_hours"
      ? values.fixedHours ?? undefined
      : undefined,
  rate:
    values.calculationType === "percent_of_total"
      ? values.rate ?? undefined
      : undefined,
  "filter-type": values.filterType,
  "filter-id": values.filterId ?? undefined,
  "allocation-method": values.allocationMethod ?? undefined,
  "assign-effort-to-service": values.assignEffortToService,
  "project-variables": governanceVariablesToApi(values.projectVariables),
  ...(configurable && {
    "minimum-hours": values.minimumHours ?? 0,
    // Rules only apply to percent_of_total; clear them like rate is cleared.
    "rate-rules":
      values.calculationType === "percent_of_total"
        ? rateRulesToApi(values.rateRules)
        : [],
  }),
});

const governanceRelationships = (values: GovernanceFormValues) => ({
  resource: values.resourceId
    ? { data: { type: "resources", id: values.resourceId } }
    : undefined,
  phase:
    values.allocationMethod === "select_phase" && values.phaseId
      ? { data: { type: "phases", id: values.phaseId } }
      : undefined,
  "service-category":
    values.allocationMethod === "select_category" && values.sectionId
      ? {
          data: {
            type: "service-categories",
            id: values.sectionId,
          },
        }
      : undefined,
});

export const useGovernanceMutations = () => {
  const [create, createState] = useV2CreateGovernanceMutation();
  const [update, updateState] = useV2UpdateGovernanceMutation();
  const [destroy, destroyState] = useV2DeleteGovernanceMutation();
  const [restore, restoreState] = useV2RestoreGovernanceByIdMutation();
  const [purge, purgeState] = useV2PurgeGovernanceByIdMutation();
  const [move, moveState] = useV2ChangeGovernancePositionByIdMutation();
  const configurable = useConfigurableGovernance();

  return {
    create: (values: GovernanceFormValues) =>
      create({
        body: {
          data: {
            type: "governances",
            attributes: governanceAttributes(values, configurable),
            relationships: governanceRelationships(values),
          },
        },
      }).unwrap(),

    update: (id: string, values: GovernanceFormValues) =>
      update({
        id: id,
        body: {
          data: {
            type: "governances",
            id: id,
            attributes: governanceAttributes(values, configurable),
            relationships: governanceRelationships(values),
          },
        },
      }).unwrap(),

    destroy: (id: string) => destroy({ id: id }).unwrap(),
    restore: (id: string) => restore({ id: id }).unwrap(),
    purge: (id: string) => purge({ id: id }).unwrap(),
    move: (id: string, position: number) =>
      move({
        id: id,
        body: {
          data: {
            type: "governances",
            id: id,
            attributes: { position },
          },
        } as Parameters<typeof move>[0]["body"],
      }).unwrap(),

    states: {
      createState,
      updateState,
      destroyState,
      restoreState,
      purgeState,
      moveState,
    },
  };
};
